﻿// js/Views/Account/account-index.js
/* =========================================================
   ACCOUNT INDEX
   Path: /wwwroot/js/Views/Account/account-index.js
   
   Initialization, event listeners, and startup logic.
   
   Dependencies (load in this order):
   1. /wwwroot/js/_shared/_utils.js
   2. /wwwroot/js/Views/Account/account-validation.js
   3. /wwwroot/js/Views/Account/account-ui.js
   4. /wwwroot/js/Views/Account/account-forms.js
   5. This file (account-index.js)
   
   Load Order: LAST
   
   OPTIMIZATION: Now ensures both eventId AND slug fields are populated
   from page config to avoid server-side lookups.
   ========================================================= */

(function () {
    "use strict";


    // Get utilities
    var utils = window.AppUtils || {};
    var qs = utils.qs || window.qs;
    var qsa = utils.qsa || window.qsa;

    // Get validation functions
    var validation = (window.AccountApp && window.AccountApp.validation) || {};
    var initPasswordRequirements = validation.initPasswordRequirements;
    var validatePassword = validation.validatePassword;

    // Get UI functions
    var ui = (window.AccountApp && window.AccountApp.ui) || {};
    var showSection = ui.showSection || window.showSection;
    var setSmartFocus = ui.setSmartFocus;
    var setButtonState = ui.setButtonState;
    var preventMultipleSubmissions = ui.preventMultipleSubmissions;
    var getCurrentSection = ui.getCurrentSection;

    // Get form handlers
    var forms = (window.AccountApp && window.AccountApp.forms) || {};
    var handleLogin = forms.handleLogin;
    var handleRegister = forms.handleRegister;
    var handleForgotPassword = forms.handleForgotPassword;
    var handleResetPassword = forms.handleResetPassword;
    var routeFromEmailGate = forms.routeFromEmailGate;

    // i18n strings (from window.i18n.strings with English fallbacks)
    var _s = (window.i18n && window.i18n.strings) || {};

    console.log('[account-index] Dependencies check:', {
        utils: !!utils,
        qs: !!qs,
        validation: !!window.AccountApp?.validation,
        ui: !!window.AccountApp?.ui,
        forms: !!window.AccountApp?.forms,
        handleLogin: !!handleLogin,
        handleRegister: !!handleRegister,
        showSection: !!showSection
    });

    /* =========================================================
       HELPER: Ensure eventId and slug fields have correct values
       
       OPTIMIZATION: Populates both eventId and slug hidden fields
       so the server doesn't need to look up the slug from eventId.
       ========================================================= */

    /**
     * Ensures all eventId and slug hidden fields have correct values from config.
     * This fixes issues where cloneNode or rendering might leave fields empty,
     * and ensures slug is passed to avoid server-side lookups.
     */
    function ensureEventFields() {
        var configEventId = window.ACCOUNT?.eventId;
        var configSlug = window.ACCOUNT?.slug;


        // Fix eventId fields
        if (configEventId && configEventId > 0) {
            var eventIdFields = document.querySelectorAll('input[name="eventId"]');
            eventIdFields.forEach(function (input) {
                var currentValue = input.value;
                if (!currentValue || currentValue === '0' || currentValue === '') {
                    input.value = configEventId;
                }
            });
        } else {
            console.warn('[account-index] No valid eventId in config:', configEventId);
        }

        // Fix/add slug fields (OPTIMIZATION: server won't need to look this up)
        if (configSlug) {
            var slugFields = document.querySelectorAll('input[name="slug"]');

            if (slugFields.length > 0) {
                // Update existing slug fields
                slugFields.forEach(function (input) {
                    var currentValue = input.value;
                    if (!currentValue || currentValue === '') {
                        input.value = configSlug;
                    }
                });
            } else {
                // No slug fields exist - add them to all forms
                var forms = document.querySelectorAll('form[action*="Account"]');
                forms.forEach(function (form) {
                    // Check if this form already has a slug input
                    if (!form.querySelector('input[name="slug"]')) {
                        var slugInput = document.createElement('input');
                        slugInput.type = 'hidden';
                        slugInput.name = 'slug';
                        slugInput.value = configSlug;
                        form.appendChild(slugInput);
                    }
                });
            }
        } else {
            console.warn('[account-index] No slug in config - server will need to look it up');
        }
    }

    /**
     * Legacy function name for backward compatibility
     * @deprecated Use ensureEventFields() instead
     */
    function ensureEventIdFields() {
        ensureEventFields();
    }

    /* =========================================================
       HELPER: Attach form handler without cloning
       ========================================================= */

    /**
     * Attaches a submit handler to a form without cloning.
     * Uses a flag to prevent duplicate handler attachment.
     * @param {Element} form - The form element
     * @param {Function} handler - The submit handler function
     * @param {string} formName - Name for logging
     */
    function attachFormHandler(form, handler, formName) {
        if (!form || !handler) return;

        // Check if handler already attached
        if (form.dataset.handlerAttached === 'true') {
            return;
        }

        form.addEventListener('submit', function (e) {
            handler(e);
        });

        form.dataset.handlerAttached = 'true';
    }

    /* =========================================================
       Reset Password Validation
       ========================================================= */

    function validateResetPasswordForm() {
        var passwordInput = qs('#reset-password-form input[name="Password"]') ||
            qs('#reset-password-form-el input[name="Password"]') ||
            qs('#ResetPassword_Password');
        var confirmInput = qs('#reset-password-form input[name="ConfirmPassword"]') ||
            qs('#reset-password-form-el input[name="ConfirmPassword"]') ||
            qs('#ResetPassword_ConfirmPassword');
        var submitBtn = qs('#reset-password-form button[type="submit"]') ||
            qs('#reset-password-form-el button[type="submit"]') ||
            qs('#reset-password-submit-btn');

        if (!passwordInput || !confirmInput || !submitBtn) {
            return;
        }

        var password = passwordInput.value || '';
        var confirmPassword = confirmInput.value || '';

        var isPasswordValid = false;
        if (validatePassword) {
            var validationResult = validatePassword(password);
            isPasswordValid = validationResult.allPassed;
        } else {
            // SEC-08 fallback (mirrors PASSWORD_RULES): length 12 + upper/lower/digit/special.
            isPasswordValid = password.length >= 12 &&
                /[A-Z]/.test(password) &&
                /[a-z]/.test(password) &&
                /[0-9]/.test(password) &&
                /[^A-Za-z0-9]/.test(password);
        }

        var passwordsMatch = password.length > 0 && password === confirmPassword;

        var confirmFormGroup = confirmInput.closest('.form-group') || confirmInput.parentElement;
        var confirmErrorSpan = qs('[data-valmsg-for="ConfirmPassword"]', confirmFormGroup) ||
            confirmFormGroup.querySelector('.field-validation-error') ||
            confirmFormGroup.querySelector('.confirm-password-error');

        if (!confirmErrorSpan) {
            confirmErrorSpan = document.createElement('span');
            confirmErrorSpan.className = 'field-validation-error confirm-password-error';
            confirmFormGroup.appendChild(confirmErrorSpan);
        }

        if (confirmPassword.length > 0) {
            if (passwordsMatch) {
                confirmInput.classList.remove('is-invalid');
                confirmInput.classList.add('is-valid');
                confirmErrorSpan.textContent = '';
                confirmErrorSpan.style.display = 'none';
            } else {
                confirmInput.classList.remove('is-valid');
                confirmInput.classList.add('is-invalid');
                confirmErrorSpan.textContent = _s.accPasswordsDoNotMatch || 'Passwords do not match';
                confirmErrorSpan.style.display = 'block';
            }
        } else {
            confirmInput.classList.remove('is-valid', 'is-invalid');
            confirmErrorSpan.textContent = '';
            confirmErrorSpan.style.display = 'none';
        }

        var isFormValid = isPasswordValid && passwordsMatch;
        submitBtn.disabled = !isFormValid;

        if (isFormValid) {
            submitBtn.classList.remove('btn-disabled', 'opacity-50');
        } else {
            submitBtn.classList.add('opacity-50');
        }

        return isFormValid;
    }

    function initResetPasswordValidation() {
        var passwordInput = qs('#reset-password-form input[name="Password"]') ||
            qs('#reset-password-form-el input[name="Password"]') ||
            qs('#ResetPassword_Password');
        var confirmInput = qs('#reset-password-form input[name="ConfirmPassword"]') ||
            qs('#reset-password-form-el input[name="ConfirmPassword"]') ||
            qs('#ResetPassword_ConfirmPassword');
        var submitBtn = qs('#reset-password-form button[type="submit"]') ||
            qs('#reset-password-form-el button[type="submit"]') ||
            qs('#reset-password-submit-btn');

        if (!passwordInput || !confirmInput || !submitBtn) {
            return;
        }

        submitBtn.disabled = true;
        submitBtn.classList.add('opacity-50');

        passwordInput.addEventListener('input', validateResetPasswordForm);
        confirmInput.addEventListener('input', validateResetPasswordForm);

        passwordInput.addEventListener('paste', function () {
            setTimeout(validateResetPasswordForm, 10);
        });
        confirmInput.addEventListener('paste', function () {
            setTimeout(validateResetPasswordForm, 10);
        });

        validateResetPasswordForm();
    }

    /* =========================================================
       AUTO-TRANSLATE
       ========================================================= */

    /**
     * Translates event description elements to the current language if not English.
     * Uses the free MyMemory translation API.
     */
    function translateEventDescription() {
        var lang = (window.i18n && window.i18n.lang) || 'en';
        if (lang === 'en') return;

        var elements = qsa('.event-description');
        if (!elements.length) return;

        var text = elements[0].textContent.trim();
        if (!text) return;

        var url = 'https://api.mymemory.translated.net/get?q=' +
            encodeURIComponent(text) + '&langpair=en|' + lang;

        fetch(url)
            .then(function (r) { return r.json(); })
            .then(function (data) {
                if (data.responseStatus === 200 && data.responseData && data.responseData.translatedText) {
                    elements.forEach(function (el) {
                        el.textContent = data.responseData.translatedText;
                    });
                }
            })
            .catch(function () { /* keep original text on failure */ });
    }

    /* =========================================================
       Initialization
       ========================================================= */

    document.addEventListener("DOMContentLoaded", function () {

        // ---------------------------
        // GA4: Set user role for segmentation
        // ---------------------------
        var acct = window.ACCOUNT || {};
        if (typeof gtag === 'function' && acct.gaMeasurementId) {
            gtag('set', 'user_properties', { user_role: 'visitor' });
        }

        // ---------------------------
        // FIX: Ensure eventId AND slug fields have correct values FIRST
        // ---------------------------
        ensureEventFields();

        // ---------------------------
        // URL Parameter Handling
        // ---------------------------

        var urlParams = new URLSearchParams(window.location.search);

        var isResetFlow = urlParams.has('code') && urlParams.has('email');

        if (isResetFlow) {
            showSection("reset-password");
            setTimeout(initResetPasswordValidation, 50);
            // NOTE: Do NOT return early here — fall through so form
            // submit handlers (especially handleResetPassword) get attached below.
        }

        // ---------------------------
        // Initial Section Display
        // (Skip when reset flow already set the section above)
        // ---------------------------

        if (!isResetFlow) {
            var hasLoginErrors = (window.ACCOUNT && window.ACCOUNT.hasLoginErrors === true) ||
                (window.ACCOUNT && window.ACCOUNT.hasLoginErrors === "true");


            if (hasLoginErrors) {
                showSection("login", true);
            } else if (sessionStorage.getItem("showingForgotConfirmation") === "true") {
                var forgotEmail = sessionStorage.getItem("forgotEmail");
                if (forgotEmail) {
                    var emailDisplay = qs("#forgot-confirmation .email-display");
                    if (emailDisplay) {
                        emailDisplay.textContent = forgotEmail;
                    }
                }
                showSection("forgot-confirmation");
            } else {
                var currentSection = getCurrentSection ? getCurrentSection() :
                    (window.ACCOUNT && window.ACCOUNT.initialSection) || "gate";
                showSection(currentSection);

                if (currentSection === 'reset-password') {
                    setTimeout(initResetPasswordValidation, 50);
                }
            }
        }

        // ---------------------------
        // Auto-translate event description
        // ---------------------------
        translateEventDescription();

        // ---------------------------
        // Email Gate Event Listeners
        // ---------------------------

        var gateBtn = qs("#email-gate-continue");

        if (gateBtn && routeFromEmailGate) {
            gateBtn.addEventListener("click", routeFromEmailGate);
        }

        var gateInput = qs("#email-gate-input");
        if (gateInput && routeFromEmailGate) {
            gateInput.addEventListener("keydown", function (e) {
                var isProcessing = (ui.getIsProcessing && ui.getIsProcessing()) || false;
                if (e.key === "Enter" && !isProcessing) {
                    e.preventDefault();
                    routeFromEmailGate();
                }
            });
        }

        // ---------------------------
        // Form Event Listeners (NO CLONING)
        // ---------------------------

        // Login form
        var loginForm = qs("#login-form-el");

        if (loginForm) {
            attachFormHandler(loginForm, handleLogin, 'login form');
        }

        // Register form
        var registerForm = qs("#register-form-el");

        if (registerForm) {
            attachFormHandler(registerForm, handleRegister, 'register form');
        }

        // Forgot password form
        var forgotForm = qs("#forgot-form-el");

        if (forgotForm) {
            attachFormHandler(forgotForm, handleForgotPassword, 'forgot form');
        }

        // Reset password form
        var resetForm = qs("#reset-password-form form") || qs("#reset-password-form-el");

        if (resetForm) {
            attachFormHandler(resetForm, handleResetPassword, 'reset form');
        }

        // ---------------------------
        // Section Link Handlers
        // ---------------------------

        qsa('a[onclick*="showSection"]').forEach(function (link) {
            link.addEventListener("click", function () {
                sessionStorage.removeItem("showingForgotConfirmation");
                sessionStorage.removeItem("forgotEmail");
            });
        });

        // ---------------------------
        // Social Auth Handlers
        // ---------------------------

        qsa('.social-form').forEach(function (form) {
            var button = form.querySelector('button[type="submit"]');
            if (button && setButtonState) {
                form.addEventListener('submit', function () {
                    var isProcessing = (ui.getIsProcessing && ui.getIsProcessing()) || false;
                    if (!isProcessing) {
                        var provider = button.getAttribute('data-provider');
                        var connectingText = (_s.accConnectingTo || 'Connecting to {0}…').replace('{0}', provider);
                        setButtonState(button, 'loading', connectingText);

                        // GA4: Track OAuth login attempt
                        var acct = window.ACCOUNT || {};
                        if (typeof gtag === 'function' && acct.gaMeasurementId) {
                            gtag('event', 'login', {
                                method: provider,
                                event_name: acct.eventName,
                                organization_name: acct.organizationName
                            });
                        }
                    }
                });
            }
        });

        // ---------------------------
        // Password Requirements Init
        // ---------------------------

        if (initPasswordRequirements) {
            initPasswordRequirements();
        }

        // ---------------------------
        // Clear Errors on Input
        // ---------------------------

        var initClearOnInput = validation.initClearOnInput;
        if (initClearOnInput) {
            initClearOnInput();
        }

        // ---------------------------
        // Initial Focus
        // ---------------------------

        var currentSectionName = getCurrentSection ? getCurrentSection() :
            (window.ACCOUNT && window.ACCOUNT.initialSection) || "gate";

        if ((currentSectionName === 'login' || currentSectionName === 'register') && setSmartFocus) {
            setSmartFocus(currentSectionName);
        }

        // ---------------------------
        // Summary Log
        // ---------------------------

        console.log('[account-index] ACCOUNT config:', {
            eventId: window.ACCOUNT?.eventId,
            slug: window.ACCOUNT?.slug,
            hasLoginErrors: window.ACCOUNT?.hasLoginErrors,
            initialSection: window.ACCOUNT?.initialSection
        });
        console.log('[account-index] Forms attached:', {
            login: !!qs("#login-form-el"),
            register: !!qs("#register-form-el"),
            forgot: !!qs("#forgot-form-el"),
            reset: !!(qs("#reset-password-form form") || qs("#reset-password-form-el"))
        });
        console.log('[account-index] Handlers available:', {
            handleLogin: !!handleLogin,
            handleRegister: !!handleRegister,
            handleForgotPassword: !!handleForgotPassword,
            handleResetPassword: !!handleResetPassword
        });
    });

    /* =========================================================
       Expose for external use
       ========================================================= */

    window.AccountApp = window.AccountApp || {};
    window.AccountApp.resetPasswordValidation = {
        validate: validateResetPasswordForm,
        init: initResetPasswordValidation
    };

    // Expose helper for debugging
    window.AccountApp.ensureEventFields = ensureEventFields;


})();